import {PayoutPurposeCode} from '@sollapay/enums';
import {FieldInput, FieldSelect} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {AiFlashWrapper} from '@/features/ai-extraction';
import {generatePayoutDescription, withDebugFieldChange} from '@/features/debug-test-data';

import type {PayeeKind} from '../utils/creditor-account.utils';
import type {useFieldAiProps} from '@/features/ai-extraction';
import type {FieldSelectOption} from '@sollapay/ui/components';

const PREFIX = 'view.payouts.create.details';

export interface PurposeFieldProps {
  payeeKind: PayeeKind;
  description: string;
  onDescriptionChange: (value: string) => void;
  purposeCode: string;
  onPurposeCodeChange: (value: string) => void;
  purposeOtherDetail: string;
  onPurposeOtherDetailChange: (value: string) => void;
  purposeCodeOptions: FieldSelectOption[];
  isFlashing: (field: string) => boolean;
  fieldAi: ReturnType<typeof useFieldAiProps>;
  descriptionError: string | undefined;
  purposeCodeError: string | undefined;
  purposeOtherDetailError: string | undefined;
}

/**
 * Purpose of transfer — locked before a payee is picked, free text for a beneficiary, and a
 * closed dropdown (with an "other" detail reveal) for authority payees.
 */
export function PurposeField({
  payeeKind,
  description,
  onDescriptionChange,
  purposeCode,
  onPurposeCodeChange,
  purposeOtherDetail,
  onPurposeOtherDetailChange,
  purposeCodeOptions,
  isFlashing,
  fieldAi,
  descriptionError,
  purposeCodeError,
  purposeOtherDetailError
}: PurposeFieldProps) {
  const {t} = useTranslation('trustAccounts');

  if (payeeKind === 'none') {
    return (
      <FieldInput
        label={t(`${PREFIX}.purpose`)}
        placeholder={t(`${PREFIX}.purposeLockedPlaceholder`)}
        value=""
        onChange={() => {}}
        disabled
      />
    );
  }

  if (payeeKind === 'beneficiary') {
    return (
      <FieldInput
        label={t(`${PREFIX}.purpose`)}
        placeholder={t(`${PREFIX}.purposePlaceholder`)}
        value={description}
        onChange={e => onDescriptionChange(e.target.value)}
        cornerBadge={withDebugFieldChange(
          undefined,
          onDescriptionChange,
          generatePayoutDescription
        )}
        error={descriptionError}
        maxLength={34}
        required
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <AiFlashWrapper field="purposeCode" fieldAi={fieldAi} isFlashing={isFlashing}>
        {ai => (
          <FieldSelect
            label={t(`${PREFIX}.purpose`)}
            placeholder={t(`${PREFIX}.purposeSelectPlaceholder`)}
            options={purposeCodeOptions}
            value={purposeCode}
            onValueChange={val => {
              ai.onAiChange();
              onPurposeCodeChange(val);
            }}
            cornerBadge={ai.aiIndicator}
            isLoading={ai.aiGenerating}
            error={purposeCodeError}
            required
          />
        )}
      </AiFlashWrapper>
      {purposeCode === PayoutPurposeCode.OTHER && (
        <FieldInput
          label={t(`${PREFIX}.purposeOtherDetailLabel`)}
          placeholder={t(`${PREFIX}.purposeOtherDetailPlaceholder`)}
          value={purposeOtherDetail}
          onChange={e => onPurposeOtherDetailChange(e.target.value)}
          error={purposeOtherDetailError}
          required
        />
      )}
    </div>
  );
}
